import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  listStores,
  listStorePage,
  getStore,
  createStore,
  updateStore,
  deleteStore,
} from "./api";
import type {
  CreateStorePayload,
  StorePageQuery,
  UpdateStorePayload,
} from "./types";

const QUERY_KEY = ["stores"];

export function useStores(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listStores(100, 0),
    enabled: options.enabled ?? true,
  });
}

export function useBuyerStorePages(enabled = true) {
  return useInfiniteQuery({
    queryKey: [...QUERY_KEY, "buyer-pages"],
    queryFn: ({ pageParam, signal }) =>
      listStorePage({ limit: 100, offset: pageParam, search: "" }, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.offset + lastPage.items.length < lastPage.total
        ? lastPage.offset + lastPage.items.length
        : undefined,
    enabled,
  });
}

export function useStore(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...QUERY_KEY, "detail", id],
    queryFn: () => getStore(id!),
    enabled: enabled && Boolean(id),
  });
}

function normalizeStorePageQuery(query: StorePageQuery): StorePageQuery {
  return {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
  };
}

export function useStorePage(query: StorePageQuery, enabled = true) {
  const normalizedQuery = normalizeStorePageQuery(query);
  const lastSuccessfulPage = useRef<
    Awaited<ReturnType<typeof listStorePage>> | undefined
  >(undefined);

  const result = useQuery({
    queryKey: [...QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) => listStorePage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (result.isSuccess && !result.isPlaceholderData) {
      lastSuccessfulPage.current = result.data;
    }
  }, [result.data, result.isPlaceholderData, result.isSuccess]);

  if (result.isError && !result.data && lastSuccessfulPage.current) {
    return {
      ...result,
      data: lastSuccessfulPage.current,
      isPlaceholderData: true,
    };
  }

  return result;
}

export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStorePayload) => createStore(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStorePayload;
    }) => updateStore(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
